import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  MICE_IN_FLIGHT_STATUSES,
  buildTravelers,
  countByColor,
  countByPhase,
  type MiceAlertFilter,
  type MiceColor,
  type MiceNeedView,
  type MiceNeedStatus,
  type MicePhaseCounts,
  type MicePhaseFilter,
  type MiceTraveler,
  type MiceTravelersPage,
} from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import { useOrgContext } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';
import { REFETCH_MS } from './use_mice_orders';

/** Viajantes por página na lista; "Mostrar mais" busca a próxima. */
export const TRAVELERS_PAGE_SIZE = 100;

/** Status de necessidade que o worker ainda vai mudar. */
const NEED_IN_FLIGHT: ReadonlySet<MiceNeedStatus> = new Set<MiceNeedStatus>([
  'validating',
  'recommending',
  'reconsidering',
]);

/** O que o servidor filtra: fase, busca por nome, e-mail ou voo, alerta, origem e cor. */
export type MiceTravelersFilter = {
  phase: MicePhaseFilter;
  q: string;
  alert: MiceAlertFilter;
  origin: string;
  color: MiceColor | 'any';
};

const moving = (t: MiceTraveler): boolean =>
  (t.need !== null && NEED_IN_FLIGHT.has(t.need.status)) ||
  t.orders.some((o) => MICE_IN_FLIGHT_STATUSES.includes(o.order.status));

/** Algo anda: uma linha carregada, ou viajantes com o agente (a fila automática ainda os pega,
 *  mesmo fora das linhas carregadas ou do filtro: os verdes novos chegam sem recarregar). */
const inFlight = (pages: readonly MiceTravelersPage[] | undefined): boolean =>
  !!pages && pages.some((p) => p.counts.agent > 0 || p.items.some(moving));

/**
 * Viajantes do evento, filtrados e paginados no servidor; as páginas já lidas se acumulam e são
 * atualizadas enquanto algo anda. Trocar o filtro mantém a lista anterior até a nova chegar.
 */
export function useMiceTravelers(
  projectId: string,
  filter: MiceTravelersFilter,
): {
  travelers: MiceTraveler[];
  /** Viajantes que batem a fase e a busca, em todas as páginas. */
  total: number;
  counts: MicePhaseCounts;
  /** Recomendações de cada cor em "Para emitir"; ignora o filtro de cor. */
  colorCounts: Record<MiceColor, number>;
  /** Origens do evento inteiro: as opções do filtro de origem. */
  origins: string[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
} {
  const { orgId } = useOrgContext();
  const query = useInfiniteQuery({
    queryKey: queryKeys.mice.travelers(orgId, projectId, filter),
    queryFn: ({ pageParam }) =>
      miceRepository.listTravelers(orgId, projectId, {
        ...filter,
        page: pageParam,
        pageSize: TRAVELERS_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (inFlight(q.state.data?.pages) ? REFETCH_MS : false),
    refetchIntervalInBackground: false,
  });
  const pages = query.data?.pages ?? [];
  const last = pages.at(-1);
  return {
    travelers: pages.flatMap((p) => p.items),
    total: last?.total ?? 0,
    counts: last?.counts ?? countByPhase([]),
    colorCounts: last?.colorCounts ?? countByColor([]),
    origins: last?.origins ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: query.hasNextPage,
    loadMore: () => {
      fireAndForget(query.fetchNextPage());
    },
  };
}

/**
 * Um viajante fora da lista filtrada (o aprovado que a revisão da compra mandou completar): lido
 * pelo e-mail exato, em qualquer fase. Até a resposta, o painel abre com os dados da necessidade.
 */
export function useMiceTraveler(projectId: string, need: MiceNeedView | null): MiceTraveler | null {
  const { orgId } = useOrgContext();
  const query = useQuery({
    queryKey: queryKeys.mice.traveler(orgId, projectId, need?.id ?? ''),
    queryFn: async () => {
      const page = await miceRepository.listTravelers(orgId, projectId, {
        phase: 'all',
        // `email` é exato e mascarado no log; o `q` (busca livre) levaria o e-mail inteiro.
        email: need?.travelerEmail,
        alert: 'any',
        origin: 'any',
        color: 'any',
        page: 1,
        pageSize: TRAVELERS_PAGE_SIZE,
      });
      return page.items.find((t) => t.id === need?.id) ?? null;
    },
    enabled: need !== null,
    placeholderData: () =>
      need ? (buildTravelers([need], [], { premisesChecked: false })[0] ?? null) : null,
  });
  return need ? (query.data ?? null) : null;
}
